import React, { useMemo, useState } from "react";
import { FaStar } from "react-icons/fa";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useNavigate } from "react-router-dom";
import { CurrencySymbol } from "../../../utils/enums";

const INITIAL_VISIBLE = 4;

const toCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatSoldPrice = (value) => {
  const amount = toCount(value);
  if (amount === 0) return `${CurrencySymbol.NGN} 0`;
  if (amount >= 1_000_000) {
    return `${CurrencySymbol.NGN} ${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  }
  if (amount >= 10_000) {
    return `${CurrencySymbol.NGN} ${Math.round(amount / 1_000)}k`;
  }
  return `${CurrencySymbol.NGN} ${amount.toLocaleString("en-US")}`;
};

const getAgentName = (agent) => {
  if (agent?.name) return agent.name;
  const full = [agent?.first_name, agent?.last_name].filter(Boolean).join(" ");
  return full || "Team member";
};

const getReviewStats = (reviews) => {
  const list = Array.isArray(reviews) ? reviews : [];
  if (list.length === 0) return { average: 0, totalCount: 0 };

  let starSum = 0;
  let ratedCount = 0;

  for (const review of list) {
    const star = Number(review?.star ?? review?.rating);
    if (Number.isFinite(star) && star > 0) {
      ratedCount += 1;
      starSum += Math.min(5, star);
    }
  }

  const average =
    ratedCount > 0 ? Math.round((starSum / ratedCount) * 10) / 10 : 0;

  return { average, totalCount: list.length };
};

const normalizeAgent = (agent) => {
  const { average, totalCount } = getReviewStats(agent?.reviews);
  const propertiesSold = toCount(
    agent?.propertiesSoldAsLeadAgent ??
    agent?.property_sold ??
    agent?.properties_sold_lead,
  );
  const medianSold = toCount(
    agent?.medianSoldPrice ?? agent?.medianPrice ?? agent?.median_sold_price,
  );

  return {
    _id: agent?._id ?? agent?.id,
    name: getAgentName(agent),
    job_title: agent?.job_title?.trim() || "—",
    profileImg: agent?.profileImg,
    propertiesSold,
    medianPrice: formatSoldPrice(medianSold),
    averageRating: average,
    reviewCount: totalCount,
  };
};

const AboutCard = ({ AgentsInfo, AgencyData }) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const agencyName =
    AgencyData?.[0]?.agency_name ||
    AgencyData?.[0]?.principal_name ||
    "this agency";

  const teamMembers = useMemo(() => {
    const fromAgentsInfo = Array.isArray(AgentsInfo) ? AgentsInfo : [];
    const fromAgency = Array.isArray(AgencyData?.[0]?.agents)
      ? AgencyData[0].agents
      : [];
    const source = fromAgentsInfo.length > 0 ? fromAgentsInfo : fromAgency;
    return source.map(normalizeAgent);
  }, [AgentsInfo, AgencyData]);

  const totalCount = teamMembers.length;
  const visibleMembers = showAll
    ? teamMembers
    : teamMembers.slice(0, INITIAL_VISIBLE);
  const visibleCount = visibleMembers.length;
  const hasMore = totalCount > INITIAL_VISIBLE;

  return (
    <div className="flex flex-col justify-start items-start bg-[#FFFFFF] md:rounded-2xl shadow-md hover:shadow-lg p-4 md:p-6">
      <div className="text-[#000929] font-semibold text-2xl">About the team</div>
      <div className="text-[#000929] font-normal text-base">
        Showing {visibleCount} of {totalCount} team members from {agencyName}
      </div>

      {totalCount === 0 ? (
        <div className="mt-4 w-full rounded-lg border border-[#EAEAEA] bg-[#F3F6F9] px-4 py-8 text-center text-sm text-[#737373]">
          No team members to display yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 w-full">
          {visibleMembers.map((agent) => (
            <div
              key={agent._id}
              className="p-4 border border-[#EAEAEA] rounded-lg w-full cursor-pointer hover:border-[#000929]/20 transition-colors"
              onClick={() => agent._id && navigate(`/agent-profile/${agent._id}`)}
            >
              <div className="flex flex-row md:flex-col lg:flex-row gap-3 mb-3">
                <div>
                  {agent.profileImg ? (
                    <LazyLoadImage
                      src={agent.profileImg}
                      alt={agent.name}
                      loading="lazy"
                      effect="blur"
                      className="w-[80px] h-[80px] aspect-square rounded-full object-cover cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (agent._id) navigate(`/agent-profile/${agent._id}`);
                      }}
                    />
                  ) : (
                    <div
                      className="w-[80px] h-[80px] rounded-full bg-[#F3F6F9] flex items-center justify-center text-[#000929] font-semibold text-xl cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (agent._id) navigate(`/agent-profile/${agent._id}`);
                      }}
                    >
                      {agent.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex flex-col justify-center items-start gap-1">
                  <div className="text-[#000929] font-medium text-xl">
                    {agent.name}
                  </div>
                  <div className="text-[#000929] font-regular text-sm flex flex-col">
                    <p className="opacity-75">{agent.job_title}</p>
                  </div>
                  <div className="flex lg:items-center flex-row md:flex-col lg:flex-row gap-1">
                    <span className="text-sm lg:text-base font-medium text-[#000929] flex gap-1">
                      <FaStar className="text-[#C2C823] text-sm mt-1" />
                      {agent.averageRating.toFixed(1)}
                    </span>
                    <span className="opacity-50 text-sm">
                      ({agent.reviewCount}{" "}
                      {agent.reviewCount === 1 ? "review" : "reviews"})
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between gap-4">
                <div className="flex flex-col justify-start item-start bg-[#F3F6F9] rounded-lg p-3 w-full gap-1">
                  <div className="text-[#333F48] font-semibold text-lg">
                    {agent.propertiesSold}
                  </div>
                  <div className="text-[#333F48] font-light text-xs">
                    Properties sold as lead agent
                  </div>
                </div>
                <div className="flex flex-col justify-start item-start bg-[#F3F6F9] rounded-lg p-3 w-full gap-1">
                  <div className="text-[#333F48] font-semibold text-lg">
                    {agent.medianPrice}
                  </div>
                  <div className="text-[#333F48] font-light text-xs">
                    Median sold price
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center w-full">
          <button
            type="button"
            className="border border-[#EAEAEA] rounded-full w-full text-base px-6 py-3 font-light text-[#000929] mt-4 mb-[10px] hover:bg-[#FAFAFA] transition-colors"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll
              ? "Show less"
              : `Show ${totalCount - INITIAL_VISIBLE} more`}
          </button>
        </div>
      )}
    </div>
  );
};

export default AboutCard;
